import { useState } from 'react';
import type { SurveyModel } from 'survey-core';
import type { CheckInCustomizationContext } from '../types';
import { formatDosage } from './formatDosage';
import {
  INJECTION_ANCHOR_QUESTION,
  INJECTION_COUNT_QUESTION,
  MAX_INJECTION_COUNT,
} from './injectionCount.config';

type InjectionCountMode = 'input' | 'summary';

export const useInjectionCountPage = (
  model: SurveyModel,
  context: CheckInCustomizationContext,
) => {
  const { lastInjectionCount } = context;
  const hasLastCount = typeof lastInjectionCount === 'number';
  const suggested = hasLastCount
    ? Math.min(lastInjectionCount + 1, MAX_INJECTION_COUNT)
    : null;

  const existing = model.getValue(INJECTION_COUNT_QUESTION) as
    | number
    | undefined;

  const initialMode: InjectionCountMode = hasLastCount ? 'summary' : 'input';
  const [mode, setMode] = useState<InjectionCountMode>(initialMode);
  const [selected, setSelected] = useState<number | null>(
    typeof existing === 'number' ? existing : suggested,
  );

  const dosage = formatDosage(model.getValue(INJECTION_ANCHOR_QUESTION));
  const displayCount = mode === 'summary' ? existing ?? suggested : selected;
  const maxCount = MAX_INJECTION_COUNT;

  const onSubmit = () => {
    if (selected === null) return;
    model.setValue(INJECTION_COUNT_QUESTION, selected);
    setMode('summary');
  };

  const onModify = () => {
    setMode('input');
  };

  const onNext = () => {
    const valueToSave = displayCount ?? suggested;
    if (valueToSave !== null) {
      model.setValue(INJECTION_COUNT_QUESTION, valueToSave);
    }
    model.nextPage();
  };

  return {
    mode,
    dosage,
    displayCount,
    maxCount,
    selected,
    setSelected,
    onSubmit,
    onModify,
    onNext,
  };
};
